/* Poke Face — interactions. Vanilla JS, no dependencies. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches || root.classList.contains('pf-no-motion');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Shared scroll loop (one rAF per frame) ---------- */
  const scrollSubs = new Set();
  let lastY = window.scrollY;
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      const dy = y - lastY;
      scrollSubs.forEach((fn) => fn(y, dy));
      lastY = y;
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ---------- Reveal on scroll ---------- */
  const revealIO = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              (e.target._pfRevealEl || e.target).classList.add('is-in');
              revealIO.unobserve(e.target);
            }
          });
        },
        { rootMargin: '0px 0px -40px 0px', threshold: 0.01 }
      )
    : null;

  function initReveal(ctx) {
    $$('[data-pf-stagger]', ctx).forEach((group) => {
      Array.from(group.children).forEach((child, i) => {
        if (child.hasAttribute('data-pf-reveal')) child.style.setProperty('--pf-i', i % 8);
      });
    });
    $$('[data-pf-reveal], .pf-footer__giant', ctx).forEach((el) => {
      if (reduceMotion || !revealIO) return el.classList.add('is-in');
      // A fully clipped element never intersects, so masks watch their parent
      const target = el.getAttribute('data-pf-reveal') === 'mask' && el.parentElement ? el.parentElement : el;
      if (target !== el) target._pfRevealEl = el;
      revealIO.observe(target);
    });
  }

  /* ---------- Header: overlay -> solid, hide on scroll down ---------- */
  function initHeader() {
    const header = document.querySelector('.pf-header');
    if (!header) return;
    const update = (y, dy) => {
      header.classList.toggle('is-scrolled', y > 24);
      const drawerOpen = document.querySelector('.pf-drawer[open]');
      if (y > 480 && dy > 4 && !drawerOpen) header.classList.add('is-hidden');
      else if (dy < -4 || y < 480) header.classList.remove('is-hidden');
      root.style.setProperty('--pf-header-offset', header.classList.contains('is-hidden') ? '0px' : getComputedStyle(header).getPropertyValue('--pf-header-h'));
    };
    scrollSubs.add(update);
    update(window.scrollY, 0);
  }

  /* ---------- Dialogs: mobile drawer + order sheet ---------- */
  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    if (reduceMotion) return dialog.close();
    dialog.classList.add('is-closing');
    const done = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    dialog.addEventListener('animationend', done, { once: true });
    setTimeout(() => dialog.open && done(), 450);
  }

  function openDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || null;
    dialog.showModal();
    root.style.overflow = 'hidden';
  }

  function initDialogs() {
    document.addEventListener('click', (e) => {
      const orderBtn = e.target.closest('[data-pf-order]');
      if (orderBtn) {
        const sheet = document.getElementById('pf-order');
        const apps = sheet ? sheet.querySelectorAll('.pf-order__app') : [];
        if (apps.length === 1) {
          e.preventDefault();
          window.open(apps[0].href, '_blank', 'noopener');
          return;
        }
        if (sheet) {
          e.preventDefault();
          const drawer = document.querySelector('.pf-drawer[open]');
          if (drawer) drawer.close();
          const item = orderBtn.getAttribute('data-pf-order-item');
          const label = sheet.querySelector('[data-pf-order-item-label]');
          if (label) {
            label.textContent = item || '';
            label.hidden = !item;
          }
          openDialog(sheet, orderBtn);
        }
        return;
      }
      const opener = e.target.closest('[data-pf-open]');
      if (opener) {
        e.preventDefault();
        openDialog(document.getElementById(opener.getAttribute('data-pf-open')), opener);
        opener.setAttribute('aria-expanded', 'true');
        return;
      }
      const closer = e.target.closest('[data-pf-close]');
      if (closer) {
        closeDialog(closer.closest('dialog'));
        return;
      }
      // Click on backdrop closes
      if (e.target instanceof HTMLDialogElement && e.target.matches('.pf-order')) {
        const r = e.target.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) closeDialog(e.target);
      }
      // Drawer links close the drawer (anchor jumps on the same page)
      if (e.target.closest('.pf-drawer a[href]')) closeDialog(e.target.closest('dialog'));
    });

    $$('dialog.pf-drawer, dialog.pf-order').forEach((d) => {
      d.addEventListener('cancel', (e) => {
        e.preventDefault();
        closeDialog(d);
      });
      d.addEventListener('close', () => {
        root.style.overflow = '';
        $$('[aria-expanded="true"][data-pf-open="' + d.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        if (d._opener && document.contains(d._opener)) d._opener.focus({ preventScroll: true });
      });
    });
  }

  /* ---------- Open now status (UK time) ---------- */
  function ukMinutesNow() {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
      const h = Number(parts.find((p) => p.type === 'hour').value);
      const m = Number(parts.find((p) => p.type === 'minute').value);
      return h * 60 + m;
    } catch (_) {
      const d = new Date();
      return d.getHours() * 60 + d.getMinutes();
    }
  }
  const toMin = (s) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec((s || '').trim());
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  function initStatus(ctx) {
    $$('[data-pf-status]', ctx).forEach((el) => {
      const open = toMin(el.dataset.open);
      const close = toMin(el.dataset.close);
      const text = el.querySelector('.pf-status__text');
      if (open == null || close == null || !text) return;
      const now = ukMinutesNow();
      const isOpen = close > open ? now >= open && now < close : now >= open || now < close;
      el.dataset.state = isOpen ? 'open' : 'closed';
      const soon = isOpen && (close - now + 1440) % 1440 <= 45;
      text.textContent = isOpen
        ? (soon ? el.dataset.closingSoon : el.dataset.openText).replace('{close}', el.dataset.close)
        : el.dataset.closedText.replace('{open}', el.dataset.open);
    });
  }

  /* ---------- Mobile sticky order bar ---------- */
  function initBar() {
    const bar = document.querySelector('.pf-bar');
    if (!bar) return;
    const footer = document.querySelector('.pf-footer__giant');
    let footerVisible = false;
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        footerVisible = e.isIntersecting;
        update(window.scrollY);
      }).observe(footer);
    }
    const threshold = () => (document.querySelector('.pf-hero') ? window.innerHeight * 0.6 : 120);
    const update = (y) => {
      const show = y > threshold() && !footerVisible;
      bar.classList.toggle('is-visible', show);
      root.style.setProperty('--pf-bar-h', bar.offsetHeight + 'px');
    };
    scrollSubs.add(update);
    update(window.scrollY);
  }

  /* ---------- Hero video: load after page load, respect data saver ---------- */
  function initHero(ctx) {
    $$('.pf-hero video[data-src]', ctx).forEach((video) => {
      const saveData = navigator.connection && navigator.connection.saveData;
      if (reduceMotion || saveData) return;
      const start = () => {
        video.src = video.dataset.src;
        video.removeAttribute('data-src');
        video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
        video.play().catch(() => {});
      };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    });
    $$('.pf-hero__pause', ctx).forEach((btn) => {
      btn.addEventListener('click', () => {
        const video = btn.closest('.pf-hero').querySelector('video');
        if (!video) return;
        const paused = btn.getAttribute('aria-pressed') === 'true';
        if (paused) video.play();
        else video.pause();
        btn.setAttribute('aria-pressed', String(!paused));
      });
    });
  }

  /* ---------- Marquee reacts to scroll velocity ---------- */
  function initMarquee(ctx) {
    if (reduceMotion) return;
    const tracks = $$('.pf-marquee__track', ctx);
    if (!tracks.length) return;
    let boost = 0;
    let raf = null;
    const decay = () => {
      boost *= 0.92;
      tracks.forEach((t) => t.getAnimations().forEach((a) => (a.playbackRate = 1 + boost)));
      raf = Math.abs(boost) > 0.02 ? requestAnimationFrame(decay) : null;
    };
    scrollSubs.add((y, dy) => {
      boost = Math.max(-3, Math.min(4, boost + dy * 0.02));
      if (!raf) raf = requestAnimationFrame(decay);
    });
  }

  /* ---------- Carousels ---------- */
  function initCarousels(ctx) {
    $$('[data-pf-carousel]', ctx).forEach((wrap) => {
      const track = wrap.querySelector('.pf-carousel__track');
      const prev = wrap.querySelector('[data-pf-prev]');
      const next = wrap.querySelector('[data-pf-next]');
      const bar = wrap.querySelector('.pf-carousel__progress');
      if (!track) return;
      const step = () => {
        const card = track.querySelector('li');
        return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 16) : 300;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const p = max > 0 ? track.scrollLeft / max : 1;
        const visible = track.clientWidth / track.scrollWidth;
        if (bar) bar.style.setProperty('--pf-progress', Math.min(1, visible + p * (1 - visible)).toFixed(3));
        if (prev) prev.disabled = track.scrollLeft < 4;
        if (next) next.disabled = track.scrollLeft > max - 4;
      };
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- 3D tilt cards + magnetic buttons (mouse only) ---------- */
  function initPointerFx(ctx) {
    if (!finePointer || reduceMotion) return;
    $$('[data-pf-tilt]', ctx).forEach((card) => {
      const amp = Number(card.dataset.pfTilt) || 10;
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        card.style.setProperty('--ry', (x * amp).toFixed(2) + 'deg');
        card.style.setProperty('--rx', (-y * amp).toFixed(2) + 'deg');
        card.style.setProperty('--gx', ((x + 0.5) * 100).toFixed(1) + '%');
        card.style.setProperty('--gy', ((y + 0.5) * 100).toFixed(1) + '%');
      });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--ry', '0deg');
        card.style.setProperty('--rx', '0deg');
      });
    });
    $$('[data-pf-magnetic]', ctx).forEach((btn) => {
      btn.addEventListener('pointermove', (e) => {
        const r = btn.getBoundingClientRect();
        btn.style.setProperty('--pf-mx', ((e.clientX - r.left - r.width / 2) * 0.25).toFixed(1) + 'px');
        btn.style.setProperty('--pf-my', ((e.clientY - r.top - r.height / 2) * 0.35).toFixed(1) + 'px');
      });
      btn.addEventListener('pointerleave', () => {
        btn.style.setProperty('--pf-mx', '0px');
        btn.style.setProperty('--pf-my', '0px');
      });
    });
  }

  /* ---------- Scroll-linked: spinning bowl, parallax, step line ---------- */
  function initScrollFx(ctx) {
    if (reduceMotion) return;
    const items = [];
    $$('[data-pf-spin]', ctx).forEach((el) => items.push({ el, type: 'spin' }));
    $$('[data-pf-parallax]', ctx).forEach((el) => items.push({ el, type: 'parallax' }));
    $$('[data-pf-steps]', ctx).forEach((el) => items.push({ el, type: 'steps' }));
    if (!items.length) return;
    const vh = () => window.innerHeight;
    const run = () => {
      items.forEach(({ el, type }) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh() + 200) return;
        const p = (vh() - r.top) / (vh() + r.height); // 0 -> 1 while passing through viewport
        if (type === 'spin') el.style.setProperty('--pf-spin', (p * 120 - 60).toFixed(2) + 'deg');
        if (type === 'parallax') el.style.setProperty('--pf-parallax', ((p - 0.5) * -60).toFixed(1) + 'px');
        if (type === 'steps') {
          const q = Math.min(1, Math.max(0, (vh() * 0.7 - r.top) / r.height));
          el.style.setProperty('--pf-line-progress', q.toFixed(3));
        }
      });
    };
    scrollSubs.add(run);
    run();
  }

  /* ---------- Count up numbers ---------- */
  function initCountUp(ctx) {
    $$('[data-pf-count]', ctx).forEach((el) => {
      const target = parseFloat(el.dataset.pfCount);
      if (isNaN(target) || reduceMotion || !('IntersectionObserver' in window)) return;
      const decimals = (el.dataset.pfCount.split('.')[1] || '').length;
      const io = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const dur = 1400;
        const tick = (t) => {
          const k = Math.min(1, (t - t0) / dur);
          const eased = 1 - Math.pow(1 - k, 4);
          el.textContent = (target * eased).toFixed(decimals);
          if (k < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
      io.observe(el);
    });
  }

  /* ---------- Lazy map ---------- */
  function initMaps(ctx) {
    $$('iframe[data-pf-map]', ctx).forEach((frame) => {
      const load = () => {
        frame.addEventListener('load', () => frame.classList.add('is-loaded'), { once: true });
        frame.src = frame.dataset.pfMap;
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) {
          io.disconnect();
          load();
        }
      }, { rootMargin: '300px' });
      io.observe(frame);
    });
  }

  /* ---------- Menu page: tabs, scrollspy, dietary filters ---------- */
  function initMenu() {
    const nav = document.querySelector('[data-pf-menu-nav]');
    if (!nav) return;
    const tabs = nav.querySelector('.pf-tabs');
    const indicator = nav.querySelector('.pf-tabs__indicator');
    const cats = $$('[data-pf-menu-category]');
    if (!tabs || !cats.length) return;

    tabs.querySelectorAll('.pf-tab').forEach((t) => t.remove());
    const links = cats.map((cat) => {
      const a = document.createElement('a');
      a.className = 'pf-tab';
      a.href = '#' + cat.id;
      a.textContent = cat.dataset.title;
      tabs.appendChild(a);
      return a;
    });

    let current = null;
    const setActive = (link, scrollTab = true) => {
      if (!link || link === current) return;
      current = link;
      links.forEach((l) => l.setAttribute('aria-current', String(l === link)));
      indicator.style.setProperty('--x', link.offsetLeft + 'px');
      indicator.style.setProperty('--w', link.offsetWidth + 'px');
      if (scrollTab) tabs.scrollTo({ left: link.offsetLeft - 24, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    let lock = false;
    links.forEach((link) =>
      link.addEventListener('click', (e) => {
        const target = document.querySelector(link.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        setActive(link);
        lock = true;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', link.getAttribute('href'));
        setTimeout(() => (lock = false), 900);
      })
    );

    const spy = () => {
      if (lock) return;
      const line = nav.getBoundingClientRect().bottom + 40;
      let active = links[0];
      cats.forEach((cat, i) => {
        if (!cat.hidden && cat.getBoundingClientRect().top <= line) active = links[i];
      });
      setActive(active);
    };
    scrollSubs.add(spy);
    window.addEventListener('resize', () => {
      if (current) {
        indicator.style.setProperty('--x', current.offsetLeft + 'px');
        indicator.style.setProperty('--w', current.offsetWidth + 'px');
      }
    });
    requestAnimationFrame(() => {
      const fromHash = location.hash && links.find((l) => l.getAttribute('href') === location.hash);
      setActive(fromHash || links[0], false);
    });

    // Dietary filters
    const filters = $$('[data-pf-filter]', nav);
    const empty = document.querySelector('[data-pf-menu-empty]');
    const search = nav.querySelector('[data-pf-menu-search]');
    const applyFilters = () => {
      const active = filters.filter((f) => f.getAttribute('aria-pressed') === 'true').map((f) => f.dataset.pfFilter.trim().toLowerCase());
      const q = search ? search.value.trim().toLowerCase() : '';
      let total = 0;
      cats.forEach((cat, i) => {
        let shown = 0;
        $$('[data-pf-item]', cat).forEach((item) => {
          const tags = (item.dataset.tags || '').toLowerCase().split('|').map((t) => t.trim());
          const text = ((item.dataset.name || '') + ' ' + (item.dataset.desc || '') + ' ' + (item.dataset.cat || '')).toLowerCase();
          const ok = active.every((a) => tags.includes(a)) && (!q || q.split(/\s+/).every((w) => text.includes(w)));
          item.hidden = !ok;
          if (ok) shown++;
        });
        cat.hidden = shown === 0;
        links[i].hidden = shown === 0;
        total += shown;
      });
      if (empty) empty.hidden = total > 0;
      current = null;
      spy();
    };
    filters.forEach((f) =>
      f.addEventListener('click', () => {
        f.setAttribute('aria-pressed', String(f.getAttribute('aria-pressed') !== 'true'));
        applyFilters();
      })
    );
    const reset = document.querySelector('[data-pf-filter-reset]');
    reset && reset.addEventListener('click', () => {
      filters.forEach((f) => f.setAttribute('aria-pressed', 'false'));
      if (search) search.value = '';
      applyFilters();
    });
    if (search) {
      let t;
      search.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          applyFilters();
          const first = cats.find((c) => !c.hidden);
          if (search.value.trim() && first) first.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }, 180);
      });
    }

    // Surprise me: shuffle through dishes, land on one, open it
    const surprise = nav.querySelector('[data-pf-surprise]');
    surprise && surprise.addEventListener('click', () => {
      const pool = $$('[data-pf-item]').filter((i) => !i.hidden);
      if (!pool.length || surprise.classList.contains('is-rolling')) return;
      const pick = pool[Math.floor(Math.random() * pool.length)];
      surprise.classList.add('is-rolling');
      pick.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      setTimeout(() => {
        surprise.classList.remove('is-rolling');
        pick.classList.remove('is-picked');
        void pick.offsetWidth;
        pick.classList.add('is-picked');
        setTimeout(() => window.pfOpenDish && window.pfOpenDish(pick), reduceMotion ? 0 : 450);
      }, reduceMotion ? 0 : 700);
    });
  }

  /* ---------- Dish popup ---------- */
  function initDish() {
    const dialog = document.getElementById('pf-dish');
    if (!dialog) return;
    const q = (sel) => dialog.querySelector(sel);
    const media = q('[data-pf-dish-media]');
    const img = q('[data-pf-dish-img]');
    const body = q('[data-pf-dish-body]');
    const sheet = q('[data-pf-dish-sheet]');
    const els = {
      cat: q('[data-pf-dish-cat]'),
      name: q('[data-pf-dish-name]'),
      price: q('[data-pf-dish-price]'),
      kcal: q('[data-pf-dish-kcal]'),
      tags: q('[data-pf-dish-tags]'),
      desc: q('[data-pf-dish-desc]'),
      badge: q('[data-pf-dish-badge]'),
      more: q('[data-pf-dish-more]'),
      moreWrap: q('[data-pf-dish-more-wrap]'),
      prev: q('[data-pf-dish-prev]'),
      next: q('[data-pf-dish-next]'),
      share: q('[data-pf-dish-share]'),
      shareLabel: q('[data-pf-dish-share-label]'),
    };
    let list = [];
    let index = -1;
    let current = null;

    const groupFor = (card) => {
      const group = card.closest('[data-pf-dish-group]');
      const scope = group ? $$('[data-name]', group).filter((c) => c.hasAttribute('data-pf-dish') || c.hasAttribute('data-pf-orbit-item')) : [];
      return scope.length > 1 ? scope.filter((c) => !c.hidden) : $$('[data-pf-dish]').filter((c) => !c.hidden);
    };

    const fill = (card) => {
      const d = card.dataset;
      current = card;
      const isCut = (d.img || '').includes('pf-cut-');
      media.classList.toggle('is-contain', d.fit === 'contain' && !isCut);
      media.classList.toggle('is-cut', isCut);
      const cardImg = card.querySelector('img');
      const quick = cardImg && cardImg.complete && cardImg.currentSrc ? cardImg.currentSrc : '';
      img.src = quick || d.img || '';
      img.alt = d.name || '';
      if (d.img && quick && quick !== d.img) {
        const pre = new Image();
        pre.onload = () => { if (current === card) img.src = d.img; };
        pre.src = d.img;
      }
      els.cat.textContent = d.cat || '';
      els.cat.hidden = !d.cat;
      els.name.textContent = d.name || '';
      els.price.textContent = d.price ? '£' + d.price : '';
      els.kcal.textContent = d.kcal ? d.kcal + ' kcal' : '';
      els.desc.textContent = d.desc || '';
      els.badge.textContent = d.badge || '';
      els.badge.hidden = !d.badge;
      els.tags.innerHTML = '';
      (d.dishTags || '').split('|').map((t) => t.trim()).filter(Boolean).forEach((t) => {
        const chip = document.createElement('span');
        chip.className = 'pf-chip';
        chip.textContent = t;
        els.tags.appendChild(chip);
      });
      // Suggestions: the next few dishes from the same group
      els.more.innerHTML = '';
      const others = list.filter((c) => c !== card);
      const start = Math.max(0, list.indexOf(card));
      const picks = [];
      for (let k = 1; k < list.length && picks.length < 3; k++) {
        const c = list[(start + k) % list.length];
        if (c !== card && c.dataset.name) picks.push(c);
      }
      picks.forEach((c) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-dish__mini';
        const im = document.createElement('img');
        im.src = c.dataset.thumb || c.dataset.img || '';
        im.alt = '';
        im.loading = 'lazy';
        im.width = 160;
        im.height = 160;
        const nm = document.createElement('span');
        nm.textContent = c.dataset.name;
        const pr = document.createElement('small');
        pr.textContent = c.dataset.price ? '£' + c.dataset.price : '';
        b.append(im, nm, pr);
        b.addEventListener('click', () => swapTo(c));
        els.more.appendChild(b);
      });
      els.moreWrap.hidden = others.length === 0;
      els.prev.hidden = els.next.hidden = list.length < 2;
      els.shareLabel.textContent = 'Share this dish';
      if (d.slug) history.replaceState(null, '', location.pathname + location.search + '#dish-' + d.slug);
    };

    const swapTo = (card) => {
      index = list.indexOf(card);
      if (reduceMotion) return fill(card);
      media.classList.add('is-swapping');
      body.classList.add('is-swapping');
      setTimeout(() => {
        fill(card);
        body.scrollTop = 0;
        sheet.scrollTop = 0;
        requestAnimationFrame(() => {
          media.classList.remove('is-swapping');
          body.classList.remove('is-swapping');
        });
      }, 200);
    };

    const open = (card) => {
      list = groupFor(card);
      index = list.indexOf(card);
      fill(card);
      body.scrollTop = 0;
      sheet.scrollTop = 0;
      if (!dialog.open) {
        dialog._opener = document.activeElement;
        dialog.showModal();
        root.style.overflow = 'hidden';
      }
    };

    // Morph the clicked card's photo into the popup photo (View Transitions), plain open otherwise
    const openWithMorph = (card) => {
      const src = card.querySelector('img');
      if (dialog.open || reduceMotion || !document.startViewTransition || !src) return open(card);
      src.style.viewTransitionName = 'pf-dish-photo';
      dialog.classList.add('is-vt');
      let vt;
      try {
        vt = document.startViewTransition(() => {
          src.style.viewTransitionName = '';
          img.style.viewTransitionName = 'pf-dish-photo';
          open(card);
        });
      } catch (_) {
        src.style.viewTransitionName = '';
        dialog.classList.remove('is-vt');
        return open(card);
      }
      vt.finished.finally(() => {
        img.style.viewTransitionName = '';
        dialog.classList.remove('is-vt');
      });
    };
    window.pfOpenDish = openWithMorph;

    const step = (dir) => {
      if (list.length < 2) return;
      index = (index + dir + list.length) % list.length;
      swapTo(list[index]);
    };
    els.prev.addEventListener('click', () => step(-1));
    els.next.addEventListener('click', () => step(1));
    dialog.addEventListener('keydown', (e) => {
      if (e.target.closest('input, textarea')) return;
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    });

    els.share.addEventListener('click', async () => {
      const url = location.href;
      const data = { title: current ? current.dataset.name : document.title, url };
      try {
        if (navigator.share) await navigator.share(data);
        else {
          await navigator.clipboard.writeText(url);
          els.shareLabel.textContent = 'Link copied!';
        }
      } catch (_) {}
    });

    const cleanup = () => {
      root.style.overflow = '';
      sheet.style.removeProperty('--pf-drag');
      if (location.hash.startsWith('#dish-')) history.replaceState(null, '', location.pathname + location.search);
      if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus({ preventScroll: true });
      dialog._opener = null;
    };
    const close = () => {
      if (!dialog.open) return;
      if (reduceMotion) {
        dialog.close();
        return cleanup();
      }
      dialog.classList.add('is-closing');
      setTimeout(() => {
        dialog.classList.remove('is-closing');
        dialog.close();
        cleanup();
      }, 260);
    };
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });
    dialog.addEventListener('click', (e) => {
      if (e.target.closest('[data-pf-close]')) {
        e.stopPropagation();
        return close();
      }
      if (e.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
      }
    });
    dialog.addEventListener('close', cleanup);

    // Swipe down to close (phones)
    let y0 = null;
    let dy = 0;
    const grabZone = media;
    grabZone.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' || e.target.closest('button')) return;
      y0 = e.clientY;
      dy = 0;
      grabZone.setPointerCapture(e.pointerId);
    });
    grabZone.addEventListener('pointermove', (e) => {
      if (y0 === null) return;
      dy = Math.max(0, e.clientY - y0);
      sheet.style.setProperty('--pf-drag', dy + 'px');
    });
    const endSwipe = () => {
      if (y0 === null) return;
      y0 = null;
      if (dy > 110) close();
      else sheet.style.setProperty('--pf-drag', '0px');
    };
    grabZone.addEventListener('pointerup', endSwipe);
    grabZone.addEventListener('pointercancel', endSwipe);

    // Open from any dish card (but let real links/buttons inside cards do their job)
    document.addEventListener('click', (e) => {
      const card = e.target.closest('[data-pf-dish]');
      if (!card) return;
      const control = e.target.closest('a, button');
      if (control && !control.hasAttribute('data-pf-dish-open')) return;
      e.preventDefault();
      openWithMorph(card);
    });

    // Deep link: /pages/menu#dish-salmon-signature-poke-bowl
    const fromHash = () => {
      if (!location.hash.startsWith('#dish-')) return;
      const slug = decodeURIComponent(location.hash.slice(6));
      const card = $$('[data-pf-dish]').find((c) => c.dataset.slug === slug);
      if (!card) return;
      card.scrollIntoView({ block: 'center' });
      card.classList.add('is-picked');
      open(card);
    };
    if (document.readyState === 'complete') fromHash();
    else window.addEventListener('load', fromHash, { once: true });
    window.addEventListener('hashchange', () => {
      if (!dialog.open) fromHash();
    });
  }

  /* ---------- Page scroll progress (header bar) ---------- */
  function initProgress() {
    const bar = document.querySelector('.pf-progress');
    if (!bar) return;
    const update = (y) => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      root.style.setProperty('--pf-page-progress', max > 0 ? (y / max).toFixed(4) : 0);
    };
    scrollSubs.add(update);
    update(window.scrollY);
  }

  /* ---------- Hero depth: layers follow the pointer ---------- */
  function initHeroDepth(ctx) {
    if (!finePointer || reduceMotion) return;
    $$('.pf-hero', ctx).forEach((hero) => {
      let raf = null;
      hero.addEventListener('pointermove', (e) => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const r = hero.getBoundingClientRect();
          hero.style.setProperty('--px', (((e.clientX - r.left) / r.width) * 2 - 1).toFixed(3));
          hero.style.setProperty('--py', (((e.clientY - r.top) / r.height) * 2 - 1).toFixed(3));
        });
      });
      hero.addEventListener('pointerleave', () => {
        hero.style.setProperty('--px', 0);
        hero.style.setProperty('--py', 0);
      });
    });
  }

  /* ---------- Scroll statement: words light up, bowls float at depth ---------- */
  function initStatement(ctx) {
    $$('[data-pf-statement]', ctx).forEach((sec) => {
      const words = $$('.pf-lit', sec);
      const floats = $$('.pf-float', sec);
      if (reduceMotion) {
        words.forEach((w) => w.classList.add('is-lit'));
        return;
      }
      const run = () => {
        const r = sec.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.bottom < -100 || r.top > vh + 100) return;
        const p = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height * 0.75)));
        const lit = Math.round(p * words.length * 1.15);
        words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
        const q = (vh - r.top) / (vh + r.height) - 0.5;
        floats.forEach((f, i) => {
          const depth = Number(f.dataset.pfDepth || 3);
          f.style.setProperty('--pf-fy', (q * -36 * depth).toFixed(1) + 'px');
          f.style.setProperty('--pf-fr', (q * (i % 2 ? -1 : 1) * 40 * depth).toFixed(1) + 'deg');
        });
      };
      scrollSubs.add(run);
      run();
    });
  }

  /* ---------- 3D bowl orbit ---------- */
  function initOrbit(ctx) {
    $$('[data-pf-orbit]', ctx).forEach((orbit) => {
      if (orbit._pfOrbit) return;
      orbit._pfOrbit = true;
      const stage = orbit.querySelector('.pf-orbit__stage');
      const items = $$('[data-pf-orbit-item]', orbit);
      const n = items.length;
      if (!n) return;
      const info = orbit.querySelector('.pf-orbit__info');
      const els = {
        name: orbit.querySelector('[data-pf-orbit-name]'),
        desc: orbit.querySelector('[data-pf-orbit-desc]'),
        price: orbit.querySelector('[data-pf-orbit-price]'),
        badge: orbit.querySelector('[data-pf-orbit-badge]'),
        order: orbit.querySelector('[data-pf-orbit-order]'),
      };
      const step = (Math.PI * 2) / n;
      let pos = 0;
      let target = 0;
      let radius = 400;
      let size = 300;
      let front = -1;
      let visible = false;
      let dragging = false;
      let hovering = false;
      let raf = null;
      let lastAuto = performance.now();
      const autoplay = orbit.dataset.autoplay === 'true' && !reduceMotion;

      const measure = () => {
        const w = stage.clientWidth;
        const h = stage.clientHeight;
        size = Math.round(Math.min(h * 0.58, w * (w < 600 ? 0.46 : 0.22), 300));
        radius = Math.min(w * (w < 600 ? 0.4 : 0.36), 520);
        stage.style.setProperty('--pf-bowl-size', size + 'px');
      };

      const showInfo = (i) => {
        const d = items[i].dataset;
        const apply = () => {
          els.name.textContent = d.name;
          els.desc.textContent = d.desc;
          els.price.textContent = d.price ? '£' + d.price.replace('£', '') : '';
          els.badge.textContent = d.badge;
          els.badge.hidden = !d.badge;
          els.order.setAttribute('data-pf-order-item', d.name);
          info.classList.remove('is-swapping');
        };
        if (reduceMotion || front === -1) return apply();
        info.classList.add('is-swapping');
        setTimeout(apply, 220);
      };

      const render = () => {
        items.forEach((item, i) => {
          let t = (i - pos) * step;
          t = Math.atan2(Math.sin(t), Math.cos(t));
          const x = Math.sin(t) * radius;
          const depth = (Math.cos(t) + 1) / 2; // 1 = front, 0 = back
          const y = (1 - depth) * -size * 0.58;
          const scale = 0.34 + 0.66 * Math.pow(depth, 1.6);
          item.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
          item.style.zIndex = String(Math.round(depth * 100));
          item.style.opacity = (0.35 + depth * 0.65).toFixed(2);
          item.style.filter = depth < 0.6 ? `blur(${((0.6 - depth) * 5).toFixed(1)}px)` : '';
        });
        const f = ((Math.round(pos) % n) + n) % n;
        if (f !== front) {
          items.forEach((it, i) => it.classList.toggle('is-front', i === f));
          showInfo(f);
          front = f;
        }
      };

      const onScreen = () => {
        const r = orbit.getBoundingClientRect();
        return r.bottom > 0 && r.top < window.innerHeight;
      };
      let lastT = 0;
      const tick = (now) => {
        raf = null;
        const dt = lastT ? Math.min(now - lastT, 250) : 16.7;
        lastT = now;
        visible = onScreen();
        if (autoplay && visible && !dragging && !hovering && now - lastAuto > 3200) {
          target = Math.round(target) + 1;
          lastAuto = now;
        }
        if (!dragging) pos += (target - pos) * (reduceMotion ? 1 : 1 - Math.pow(1 - 0.085, dt / 16.7));
        render();
        if (visible && (Math.abs(target - pos) > 0.0005 || dragging || autoplay)) raf = requestAnimationFrame(tick);
        else lastT = 0;
      };
      const kick = () => {
        if (!raf) raf = requestAnimationFrame(tick);
      };
      scrollSubs.add(() => {
        if (autoplay && !raf && onScreen()) kick();
      });

      const goTo = (i) => {
        // shortest way round to item i
        const cur = Math.round(target);
        const curIdx = ((cur % n) + n) % n;
        let diff = i - curIdx;
        if (diff > n / 2) diff -= n;
        if (diff < -n / 2) diff += n;
        target = cur + diff;
        lastAuto = performance.now();
        kick();
      };

      orbit.querySelector('[data-pf-orbit-prev]').addEventListener('click', () => goTo((front - 1 + n) % n));
      orbit.querySelector('[data-pf-orbit-next]').addEventListener('click', () => goTo((front + 1) % n));
      items.forEach((item, i) =>
        item.querySelector('.pf-orbit__bowl').addEventListener('click', () => {
          if (moved > 6) return;
          if (i === front) {
            if (window.pfOpenDish) window.pfOpenDish(items[i]);
            else els.order.click();
          } else goTo(i);
        })
      );
      orbit.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') goTo((front - 1 + n) % n);
        if (e.key === 'ArrowRight') goTo((front + 1) % n);
      });

      // Drag / swipe
      let startX = 0;
      let startPos = 0;
      let moved = 0;
      stage.addEventListener('pointerdown', (e) => {
        dragging = true;
        moved = 0;
        startX = e.clientX;
        startPos = pos;
        stage._pid = e.pointerId;
        kick();
      });
      stage.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        if (moved > 6 && !stage.classList.contains('is-dragging')) {
          stage.classList.add('is-dragging');
          try { stage.setPointerCapture(stage._pid); } catch (_) {}
        }
        pos = startPos - dx / (radius * step * 1.1);
        target = pos;
      });
      const endDrag = () => {
        if (!dragging) return;
        dragging = false;
        stage.classList.remove('is-dragging');
        target = Math.round(pos);
        lastAuto = performance.now();
        kick();
      };
      stage.addEventListener('pointerup', endDrag);
      stage.addEventListener('pointercancel', endDrag);
      stage.addEventListener('pointerenter', () => (hovering = true));
      stage.addEventListener('pointerleave', () => {
        hovering = false;
        kick();
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => {
          visible = e.isIntersecting;
          if (visible) {
            lastAuto = performance.now();
            kick();
          }
        }).observe(orbit);
      } else visible = true;
      window.addEventListener('resize', () => {
        measure();
        render();
      });
      measure();
      render();
    });
  }

  /* ---------- Online ordering for collection (Shopify cart) ---------- */
  function initCart() {
    const drawer = document.querySelector('[data-pf-cart]');
    if (!drawer) return;
    const linesEl = drawer.querySelector('[data-pf-cart-lines]');
    const emptyEl = drawer.querySelector('[data-pf-cart-empty]');
    const extras = drawer.querySelector('[data-pf-cart-extras]');
    const foot = drawer.querySelector('[data-pf-cart-foot]');
    const totalEl = drawer.querySelector('[data-pf-cart-total]');
    const timeSel = drawer.querySelector('[data-pf-cart-time]');
    const noteEl = drawer.querySelector('[data-pf-cart-note]');
    const checkout = drawer.querySelector('[data-pf-cart-checkout]');
    const toast = document.querySelector('[data-pf-toast]');
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const money = (cents) => '£' + (cents / 100).toFixed(2);
    let cart = null;
    let busy = false;

    const showToast = (msg, withLink) => {
      if (!toast) return;
      toast.innerHTML = '';
      const span = document.createElement('span');
      span.textContent = msg;
      toast.appendChild(span);
      if (withLink) {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = 'View order';
        b.addEventListener('click', openCart);
        toast.appendChild(b);
      }
      toast.hidden = false;
      toast.classList.remove('is-out');
      clearTimeout(toast._t);
      toast._t = setTimeout(() => {
        toast.classList.add('is-out');
        setTimeout(() => (toast.hidden = true), 300);
      }, 3200);
    };

    // Collection time options: today's remaining slots (UK time), or tomorrow's if closed for the day
    const buildTimes = () => {
      if (!timeSel) return;
      const toMinLocal = (s) => {
        const m = /^(\d{1,2}):(\d{2})$/.exec((s || '').trim());
        return m ? +m[1] * 60 + +m[2] : null;
      };
      const open = toMinLocal(drawer.dataset.open) ?? 690;
      const close = toMinLocal(drawer.dataset.close) ?? 1290;
      const lead = parseInt(drawer.dataset.lead, 10) || 20;
      let now;
      try {
        const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
        now = +p.find((x) => x.type === 'hour').value * 60 + +p.find((x) => x.type === 'minute').value;
      } catch (_) {
        const d = new Date();
        now = d.getHours() * 60 + d.getMinutes();
      }
      const fmt = (m) => String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
      const prev = timeSel.value || (cart && cart.attributes && cart.attributes['Collection time']) || '';
      timeSel.innerHTML = '';
      const add = (label) => {
        const o = document.createElement('option');
        o.value = o.textContent = label;
        timeSel.appendChild(o);
      };
      let start = Math.ceil(Math.max(now + lead, open + 15) / 15) * 15;
      const last = close - 15;
      if (now >= open && now < close - lead) add('As soon as possible (about ' + lead + ' min)');
      if (start <= last && now < close) {
        for (let m = start; m <= last; m += 15) add('Today ' + fmt(m));
      } else {
        for (let m = open + 15; m <= last; m += 15) add('Tomorrow ' + fmt(m));
      }
      if (prev && [...timeSel.options].some((o) => o.value === prev)) timeSel.value = prev;
    };

    const render = () => {
      const items = cart ? cart.items : [];
      linesEl.innerHTML = '';
      items.forEach((item, idx) => {
        const li = document.createElement('li');
        li.className = 'pf-cart__line';
        const img = document.createElement('img');
        img.src = item.image ? item.image + (item.image.includes('?') ? '&' : '?') + 'width=160' : '';
        img.alt = '';
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        const info = document.createElement('div');
        info.className = 'pf-cart__info';
        const name = document.createElement('span');
        name.className = 'pf-cart__name';
        name.textContent = item.product_title;
        const price = document.createElement('span');
        price.className = 'pf-cart__price';
        price.textContent = money(item.final_line_price);
        info.append(name, price);
        const qty = document.createElement('div');
        qty.className = 'pf-qty pf-qty--sm';
        qty.setAttribute('role', 'group');
        qty.setAttribute('aria-label', 'Quantity of ' + item.product_title);
        const mk = (txt, delta, label) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.textContent = txt;
          b.setAttribute('aria-label', label);
          b.addEventListener('click', () => change(idx + 1, item.quantity + delta));
          return b;
        };
        const val = document.createElement('span');
        val.textContent = item.quantity;
        qty.append(mk('−', -1, 'Decrease quantity'), val, mk('+', 1, 'Increase quantity'));
        li.append(img, info, qty);
        linesEl.appendChild(li);
      });
      const has = items.length > 0;
      emptyEl.hidden = has;
      extras.hidden = !has;
      foot.hidden = !has;
      totalEl.textContent = money(cart ? cart.total_price : 0);
      if (noteEl && cart && document.activeElement !== noteEl) noteEl.value = cart.note || '';
      // Header count + mobile bar summary
      const count = cart ? cart.item_count : 0;
      $$('[data-pf-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      const barCart = document.querySelector('[data-pf-bar-cart]');
      const barOrder = document.querySelector('[data-pf-bar-order]');
      if (barCart) {
        barCart.hidden = count === 0;
        if (barOrder) barOrder.hidden = count > 0;
        const t = barCart.querySelector('[data-pf-bar-total]');
        if (t) t.textContent = count + (count === 1 ? ' item · ' : ' items · ') + money(cart ? cart.total_price : 0);
      }
      if (has) buildTimes();
    };

    const refresh = async () => {
      try {
        const r = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        cart = await r.json();
        render();
      } catch (_) {}
    };

    const change = async (line, quantity) => {
      if (busy) return;
      busy = true;
      try {
        const r = await fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: Math.max(0, quantity) }),
        });
        cart = await r.json();
        render();
      } catch (_) {
        showToast('Sorry, something went wrong. Please try again.');
      }
      busy = false;
    };

    const addByHandle = async (handle, quantity = 1, btn) => {
      if (!handle) return false;
      btn && btn.classList.add('is-loading');
      try {
        const pr = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } });
        if (!pr.ok) throw new Error('missing');
        const product = await pr.json();
        const variant = product.variants.find((v) => v.available);
        if (!variant) {
          showToast('Sorry, this is sold out right now.');
          return false;
        }
        const r = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity }] }),
        });
        if (!r.ok) throw new Error('add');
        await refresh();
        $$('[data-pf-cart-count]').forEach((el) => {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        });
        showToast(quantity > 1 ? `${quantity} × ${product.title} added` : `${product.title} added`, true);
        return true;
      } catch (e) {
        showToast(e.message === 'missing' ? 'This dish is not available online yet. Please call us to order.' : 'Sorry, that did not work. Please try again.');
        return false;
      } finally {
        btn && btn.classList.remove('is-loading');
      }
    };
    window.pfAddToOrder = addByHandle;

    function openCart() {
      if (drawer.open) return;
      document.querySelectorAll('dialog[open]').forEach((d) => d !== drawer && d.close());
      drawer.showModal();
      root_lock(true);
      refresh();
    }
    const root_lock = (on) => (document.documentElement.style.overflow = on ? 'hidden' : '');
    const closeCart = () => {
      if (!drawer.open) return;
      drawer.classList.add('is-closing');
      setTimeout(() => {
        drawer.classList.remove('is-closing');
        drawer.close();
        root_lock(false);
      }, reduceMotion ? 0 : 240);
    };
    drawer.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeCart();
    });
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-pf-close]')) {
        e.stopPropagation();
        return closeCart();
      }
      if (e.target === drawer) closeCart();
    });
    drawer.addEventListener('close', () => root_lock(false));

    // Save note + collection time to the cart, then go to Shopify checkout
    const saveExtras = () =>
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: noteEl ? noteEl.value : '', attributes: { 'Collection time': timeSel ? timeSel.value : '', 'Order type': 'Collection – 18 Hessel Street' } }),
      });
    noteEl && noteEl.addEventListener('change', saveExtras);
    timeSel && timeSel.addEventListener('change', saveExtras);
    checkout.addEventListener('click', async () => {
      checkout.classList.add('is-loading');
      try {
        await saveExtras();
      } catch (_) {}
      location.href = root + 'checkout';
    });

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-pf-cart-open]');
      if (opener) {
        e.preventDefault();
        return openCart();
      }
      const add = e.target.closest('[data-pf-add]');
      if (add) {
        e.preventDefault();
        e.stopPropagation();
        const card = add.closest('[data-slug]');
        if (card) addByHandle(card.dataset.slug, 1, add);
      }
    });

    // Popup: quantity stepper + add button
    const dish = document.getElementById('pf-dish');
    if (dish) {
      const qtyVal = dish.querySelector('[data-pf-qty-value]');
      const addBtn = dish.querySelector('[data-pf-dish-add]');
      const priceEl = dish.querySelector('[data-pf-dish-add-price]');
      let qty = 1;
      const syncPrice = () => {
        const p = parseFloat((dish.querySelector('[data-pf-dish-price]').textContent || '').replace(/[^\d.]/g, ''));
        if (priceEl) priceEl.textContent = isNaN(p) ? '' : '£' + (p * qty).toFixed(2);
        if (qtyVal) qtyVal.textContent = qty;
      };
      dish.addEventListener('click', (e) => {
        const step = e.target.closest('[data-pf-qty]');
        if (step) {
          qty = Math.min(20, Math.max(1, qty + Number(step.dataset.pfQty)));
          syncPrice();
        }
      });
      // Reset quantity whenever a different dish is shown
      new MutationObserver(() => {
        qty = 1;
        syncPrice();
      }).observe(dish.querySelector('[data-pf-dish-name]'), { childList: true, characterData: true, subtree: true });
      addBtn &&
        addBtn.addEventListener('click', async () => {
          const handle = (location.hash.startsWith('#dish-') && location.hash.slice(6)) || '';
          const ok = await addByHandle(decodeURIComponent(handle), qty, addBtn);
          if (ok) {
            dish.close();
            document.documentElement.style.overflow = '';
            openCart();
          }
        });
    }

    refresh();
  }

  /* ---------- Init ---------- */
  function initSection(ctx) {
    initReveal(ctx);
    initStatus(ctx);
    initHero(ctx);
    initMarquee(ctx);
    initCarousels(ctx);
    initPointerFx(ctx);
    initScrollFx(ctx);
    initCountUp(ctx);
    initMaps(ctx);
    initHeroDepth(ctx);
    initStatement(ctx);
    initOrbit(ctx);
  }

  function init() {
    initHeader();
    initDialogs();
    initBar();
    initProgress();
    initDish();
    initCart();
    initMenu();
    initSection(document);
    setInterval(() => initStatus(document), 60000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run when a section is added or edited
  document.addEventListener('shopify:section:load', (e) => {
    initSection(e.target);
    if (e.target.querySelector('[data-pf-menu-category], [data-pf-menu-nav]')) initMenu();
  });
})();
